import React from 'react'
import Icon from '@components/Icon'
import {
  EXAM_RETRY_WAIT_DAYS,
  EXAM_TYPES,
  STUDENT_PROGRESS_STATUS,
} from '@helpers/constants'

// True when the student is allowed to start a new attempt, based on the
// date of the latest attempt and EXAM_RETRY_WAIT_DAYS. The wait is counted
// in calendar days (local time), so with 1 day a new attempt is allowed
// from midnight the following day.
export const retryWaitIsOver = (latestAttemptDate) => {
  if (!latestAttemptDate || EXAM_RETRY_WAIT_DAYS <= 0) return true

  const earliestRetry = new Date(latestAttemptDate)
  earliestRetry.setHours(0, 0, 0, 0)
  earliestRetry.setDate(earliestRetry.getDate() + EXAM_RETRY_WAIT_DAYS)

  return new Date() >= earliestRetry
}

export const getAttemptsLeft = (exam, attemptsUsed) => {
  return Math.max(0, exam.maxAttempts - attemptsUsed)
}

export const getAttemptsByStatus = (attempts, isPassedValue) => {
  return attempts.filter(
    (attempt) =>
      attempt.isPassed === isPassedValue ||
      (isPassedValue === null && attempt.isPassed === null)
  )
}

export const getHighestAttempt = (attempts, isPassedValue) => {
  let highestAttempt = null

  attempts.forEach((attempt) => {
    // Check if isPassed matches isPassedValue or if both are null
    const isMatch =
      attempt.isPassed === isPassedValue ||
      (isPassedValue === null && attempt.isPassed === null)

    if (isMatch) {
      const percentage = (attempt.correctCount / attempt.totalCount) * 100

      if (!highestAttempt || percentage > highestAttempt.percentage) {
        highestAttempt = { ...attempt, percentage }
      }
    }
  })

  return highestAttempt
}

export const getLatestAttempt = (attempts) => {
  if (attempts.length === 0) return null

  return attempts.reduce((latest, item) => {
    return new Date(item.updated_at) > new Date(latest.updated_at)
      ? item
      : latest
  })
}

export const getFormattedPercent = (correctCount, totalCount) => {
  const percent = (correctCount * 100) / totalCount
  const formattedPercent =
    percent % 1 === 0 ? percent : parseFloat(percent.toFixed(2))

  return `${formattedPercent}%`
}

export const anySectionHasExams = (sections) => {
  if (!Array.isArray(sections)) return false

  return sections.some(
    (section) => Array.isArray(section.exams) && section.exams.length > 0
  )
}

export const anySectionHasQuizExams = (sections) => {
  if (!Array.isArray(sections)) return false

  return sections.some(
    (section) =>
      Array.isArray(section.exams) &&
      section.exams.some((exam) => String(exam.type) === EXAM_TYPES.QUIZ.value)
  )
}

const getExamAttempts = (
  exam,
  attemptType = 'customerAttempts', // "customerAttempts" or "schoolClassAttempts"
  studentIds = []
) => {
  // Check if the specified attempt type exists and is an array
  const attempts = Array.isArray(exam[attemptType]) ? exam[attemptType] : []

  // If studentIds are provided, filter attempts by student IDs
  if (studentIds.length > 0) {
    return attempts.filter(
      (attempt) => attempt.customer && studentIds.includes(attempt.customer.id)
    )
  }

  // Return all attempts if no filtering is needed
  return attempts
}

export const anySectionHasExamAttempts = (
  sections,
  attemptType,
  studentIds = []
) => {
  return sections.some(
    (section) =>
      Array.isArray(section.exams) &&
      section.exams.some((exam) => {
        const attempts = getExamAttempts(exam, attemptType, studentIds)
        return attempts.length > 0
      })
  )
}

export const allSectionsPassed = (sections, attemptType, studentIds = []) => {
  return sections.every(
    (section) =>
      Array.isArray(section.exams) &&
      section.exams.every((exam) => {
        // Get attempts based on the specified attempt type and student IDs
        const attempts = getExamAttempts(exam, attemptType, studentIds)
        // Check if there's at least one successful attempt
        return attempts.some((attempt) => attempt.isPassed === true)
      })
  )
}

export const anySectionHasFailedExam = (
  sections,
  attemptType,
  studentIds = []
) => {
  return sections.some(
    (section) =>
      Array.isArray(section.exams) &&
      section.exams.some((exam) => {
        // Retrieve attempts based on specified attempt type and student IDs
        const attempts = getExamAttempts(exam, attemptType, studentIds)

        // Check if there is at least one passed attempt
        const hasPassedAttempt = attempts.some(
          (attempt) => attempt.isPassed === true
        )

        // Check if max attempts are reached
        const maxAttemptsReached =
          exam.maxAttempts !== null && attempts.length >= exam.maxAttempts

        // Exam is considered failed if max attempts are reached and no attempts are passed
        return maxAttemptsReached && !hasPassedAttempt
      })
  )
}

export const allSectionsCompleted = (
  sections,
  attemptType,
  studentIds = []
) => {
  return sections.every(
    (section) =>
      Array.isArray(section.exams) &&
      section.exams.every((exam) => {
        // Get attempts based on the specified attempt type and student IDs
        const attempts = getExamAttempts(exam, attemptType, studentIds)

        // Check if there's at least one successful attempt
        const hasPassedAttempt = attempts.some(
          (attempt) => attempt.isPassed === true
        )

        // Check if max attempts have been reached
        const maxAttemptsReached =
          exam.maxAttempts !== null && attempts.length >= exam.maxAttempts

        // Consider the exam completed if there's a passed attempt or max attempts are reached
        return hasPassedAttempt || maxAttemptsReached
      })
  )
}

// // ---

export const allExamsPassed = (exams, attemptType, studentIds = []) => {
  return exams.every((exam) => {
    // Get the attempts based on the specified attempt type and student IDs
    const attempts = getExamAttempts(exam, attemptType, studentIds)

    // Check if there's at least one successful attempt
    return (
      Array.isArray(attempts) &&
      attempts.some((attempt) => attempt.isPassed === true)
    )
  })
}

export const allExamsCompleted = (exams, attemptType, studentIds = []) => {
  return exams.every((exam) => {
    const attempts = getExamAttempts(exam, attemptType, studentIds)

    // Check if the exam is completed (passed or max attempts reached)
    const isPassed = attempts.some((attempt) => attempt.isPassed === true)
    const maxAttemptsReached =
      exam.maxAttempts !== null && attempts.length >= exam.maxAttempts

    return isPassed || maxAttemptsReached
  })
}

const anyExamFailed = (exams, attemptType, studentIds = []) => {
  return exams.some((exam) => {
    // Get the attempts based on the specified attempt type and student IDs
    const attempts = getExamAttempts(exam, attemptType, studentIds)

    // Check if there's at least one passed attempt
    const hasPassedAttempt = attempts.some(
      (attempt) => attempt.isPassed === true
    )

    // Check if max attempts are reached
    const maxAttemptsReached =
      exam.maxAttempts !== null && attempts.length >= exam.maxAttempts

    // Exam is considered failed if max attempts are reached and there are no passing attempts
    return maxAttemptsReached && !hasPassedAttempt
  })
}

const anyExamHasAttempt = (exams, attemptType, studentIds = []) => {
  return exams.some((exam) => {
    // Get the attempts based on the specified attempt type and student IDs
    const attempts = getExamAttempts(exam, attemptType, studentIds)

    // Check if there are any attempts
    return attempts.length > 0
  })
}

export const getFirstUncompletedSectionIndex = (
  sections,
  attemptType,
  studentIds = []
) => {
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i]

    if (Array.isArray(section.exams)) {
      // Check if all exams in the section are completed
      const allExamsAreCompleted = allExamsCompleted(
        section.exams,
        attemptType,
        studentIds
      )

      // If not all exams are completed, return the current section index
      if (!allExamsAreCompleted) {
        return i
      }
    }
  }

  // If all sections are completed, return 0 for the first section
  return 0
}

export const getSectionTagIcon = (exams, attemptType, studentIds = []) => {
  const hasExams = exams && exams.length > 0

  const allPassed = hasExams && allExamsPassed(exams, attemptType, studentIds)
  const anyFailed = hasExams && anyExamFailed(exams, attemptType, studentIds)
  const hasAttempts =
    hasExams && anyExamHasAttempt(exams, attemptType, studentIds)

  // Section has no exams
  let icon = 'SectionTagNoExams'

  if (hasExams) {
    if (allPassed) {
      icon = 'SectionTagPassedCheckmark'
    } else {
      if (anyFailed) {
        // At least on exam failed (all attempts consumed without one of them being passed)
        icon = 'SectionTagFailedCross'
      } else {
        if (hasAttempts) {
          // No failed exams, and at least one exam has attempts
          icon = 'SectionTagHasExamsHasAttempts'
        } else {
          // None of the exams has any attempts
          icon = 'SectionTagHasExamsNoAttempts'
        }
      }
    }
  }

  return icon
}

// ---

export const keepScrollPosition = async (
  functionToAwait,
  functionParameters
) => {
  // Capture current scroll position
  const mainElement = document.querySelector('main')
  let scrollPosition = 0

  if (mainElement) {
    scrollPosition = mainElement.scrollTop
  }

  await functionToAwait(functionParameters)

  // Restore the scroll position
  if (mainElement) {
    setTimeout(() => {
      mainElement.scrollTop = scrollPosition
    }, 20)

    setTimeout(() => {
      mainElement.scrollTop = scrollPosition
    }, 60)

    setTimeout(() => {
      mainElement.scrollTop = scrollPosition
    }, 120)
  }
}

// --- Functionality for teacher view

export const evaluateBookProgress = (sections, students) => {
  if (!Array.isArray(students) || students.length === 0) return {}

  // Initialize counters
  let notStartedCount = 0
  let allPassedCount = 0
  let anyFailedCount = 0
  let inProgressCount = 0

  // Iterate over each student
  students.forEach((student) => {
    const studentId = student.id

    const bookHasExams = anySectionHasExams(sections)
    const bookHasAttempts = anySectionHasExamAttempts(
      sections,
      'schoolClassAttempts',
      [studentId]
    )
    const bookIsPassed = allSectionsPassed(sections, 'schoolClassAttempts', [
      studentId,
    ])
    const bookIsFailed = anySectionHasFailedExam(
      sections,
      'schoolClassAttempts',
      [studentId]
    )
    const bookIsCompleted = allSectionsCompleted(
      sections,
      'schoolClassAttempts',
      [studentId]
    )

    if (bookHasExams) {
      if (bookIsPassed) {
        allPassedCount += 1
      } else if (bookIsCompleted) {
        if (bookIsFailed) {
          anyFailedCount += 1
        }
      } else if (bookHasAttempts) {
        inProgressCount += 1
      } else {
        notStartedCount += 1
      }
    } else {
      notStartedCount += 1
    }
  })

  // Return progress evaluation
  return {
    notStartedCount,
    allPassedCount,
    anyFailedCount,
    inProgressCount,
  }
}

export const evaluateSectionProgress = (section, students) => {
  // Initialize counters for each progress status
  let notStartedCount = 0
  let allPassedCount = 0
  let anyFailedCount = 0
  let inProgressCount = 0

  // Check if the section has exams
  const hasExams = section.exams && section.exams.length > 0

  if (!hasExams) {
    // If no exams are present in the section, all students are in the 'not started' state
    notStartedCount = students.length
  } else {
    // Iterate over each student and evaluate their status
    students.forEach((student) => {
      const studentId = student.id

      const allPassed = allExamsPassed(section.exams, 'schoolClassAttempts', [
        studentId,
      ])
      const anyFailed = anyExamFailed(section.exams, 'schoolClassAttempts', [
        studentId,
      ])
      const hasAttempts = anyExamHasAttempt(
        section.exams,
        'schoolClassAttempts',
        [studentId]
      )

      if (allPassed) {
        allPassedCount += 1
      } else if (anyFailed) {
        anyFailedCount += 1
      } else if (hasAttempts) {
        inProgressCount += 1
      } else {
        notStartedCount += 1
      }
    })
  }

  // Return the counts for each status
  return {
    notStartedCount,
    allPassedCount,
    anyFailedCount,
    inProgressCount,
  }
}

export const getStudentsByBookStatus = (sections, students, status) => {
  if (!Array.isArray(students) || students.length === 0) return []

  // Filter students by the given status
  return students.filter((student) => {
    const studentId = student.id

    const bookHasExams = anySectionHasExams(sections)
    const bookHasAttempts = anySectionHasExamAttempts(
      sections,
      'schoolClassAttempts',
      [studentId]
    )
    const bookIsPassed = allSectionsPassed(sections, 'schoolClassAttempts', [
      studentId,
    ])
    const bookIsFailed = anySectionHasFailedExam(
      sections,
      'schoolClassAttempts',
      [studentId]
    )
    const bookIsCompleted = allSectionsCompleted(
      sections,
      'schoolClassAttempts',
      [studentId]
    )

    if (!bookHasExams) {
      return status === STUDENT_PROGRESS_STATUS.NOT_STARTED
    }

    if (bookIsPassed) {
      return status === STUDENT_PROGRESS_STATUS.PASSED
    } else if (bookIsCompleted) {
      return status === STUDENT_PROGRESS_STATUS.FAILED && bookIsFailed
    } else if (bookHasAttempts) {
      return status === STUDENT_PROGRESS_STATUS.IN_PROGRESS
    } else {
      return status === STUDENT_PROGRESS_STATUS.NOT_STARTED
    }
  })
}

export const getStudentsBySectionStatus = (section, students, status) => {
  if (!Array.isArray(students) || students.length === 0) return []

  // Filter students by the given status for the section
  return students.filter((student) => {
    const studentId = student.id

    // Check section exams and student status on them
    const hasExams = Array.isArray(section.exams) && section.exams.length > 0
    const hasAttempts =
      hasExams &&
      anyExamHasAttempt(section.exams, 'schoolClassAttempts', [studentId])
    const allPassed =
      hasExams &&
      allExamsPassed(section.exams, 'schoolClassAttempts', [studentId])
    const anyFailed =
      hasExams &&
      anyExamFailed(section.exams, 'schoolClassAttempts', [studentId])

    // Determine student status based on the exams
    if (!hasExams) {
      return status === STUDENT_PROGRESS_STATUS.NOT_STARTED
    }

    if (allPassed) {
      return status === STUDENT_PROGRESS_STATUS.PASSED
    } else if (anyFailed) {
      return status === STUDENT_PROGRESS_STATUS.FAILED
    } else if (hasAttempts) {
      return status === STUDENT_PROGRESS_STATUS.IN_PROGRESS
    } else {
      return status === STUDENT_PROGRESS_STATUS.NOT_STARTED
    }
  })
}

export const getStudentAttemptsToDelete = (studentId, section) => {
  return section.exams.flatMap(
    (exam) =>
      exam.schoolClassAttempts
        .filter((attempt) => attempt.customer.id === studentId) // Filter attempts for the given student
        .map((attempt) => attempt.id) // Extract the attempt IDs
  )
}

export const getSectionByIdFromResource = (resource, sectionId) => {
  for (const book of resource.books) {
    const section = book.sections.find((section) => section.id === sectionId)
    if (section) {
      return section
    }
  }
  return null // Return null if the section is not found
}

// Components

export const StatusCorner = (props) => {
  const { passed, size = 'small' } = props

  let cornerClasses = 'absolute opacity-40 top-0 right-0 border-transparent'
  let iconClasses = 'absolute'

  const icon = passed ? 'ExamPassedCheckmark' : 'ExamFailedCross'

  if (size === 'small') {
    cornerClasses += ' border-[30px]'
    iconClasses += ' top-2.5 right-2.5 w-8 h-8'
  } else {
    cornerClasses += ' border-[50px]'
    iconClasses += ' top-6 right-4'
  }

  if (passed === true) {
    cornerClasses += ' border-t-examGreen border-r-examGreen'
  } else if (passed === false) {
    cornerClasses += ' border-t-examRed border-r-examRed'
  } else {
    cornerClasses += ' border-t-mediumGrey border-r-mediumGrey'
  }

  return (
    <>
      <div className={cornerClasses}></div>
      {passed !== undefined && <Icon icon={icon} iconClasses={iconClasses} />}
    </>
  )
}
